import {ref, computed, watch} from '@vue/composition-api'
import {useToast} from 'vue-toastification/composition'
import ToastificationContent
  from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import {useUtils as useI18nUtils} from '@core/libs/i18n'

export default function useBoshPanelsWizard(props, {emit}) {
  const toast = useToast();
  const {t} = useI18nUtils();

  const currentStep = ref(0);
  const finishAtStep = ref(0);
  const isLoading = ref(false);
  const isCreatingPanel = ref(false);
  const isOpen = ref(false);
  const profileOptions = ref([]);
  const generalStep = ref(null);
  const isInitialLoad = ref(false);

  const wizardTitle = computed(() => {
    return props.isAdd ? t("bosch.wizard.add.title") : t("bosch.wizard.edit.title");
  });

  const wizardSteps = [
    t("bosch.wizard.steps.general"),
  ];

  const wizardStepIcons = ["SettingsIcon"];

  const blankData = {
    name: null,
    panel_host: null,
    panel_passcode: null,
    panel_instance: null,
    summary: "{}",
    active: 1,
    id_unit: null,
    id_center: null,
    id_sector: null,
    id_bosch_reseller: null,
  };

  const itemData = ref(JSON.parse(JSON.stringify(blankData)));
  const originalData = ref(JSON.parse(JSON.stringify(blankData)));

  const hasChanges = computed(() => {
    if (props.isAdd) return false;
    return JSON.stringify(itemData.value) !== JSON.stringify(originalData.value);
  });

  // Propiedad computada para manejar el estado del modal sin mutar la prop
  const localIsActive = computed({
    get() {
      return props.isActive;
    },
    set(value) {
      emit("update:isActive", value);
    },
  });

  const resetData = () => {
    itemData.value = JSON.parse(JSON.stringify(blankData));
    currentStep.value = 0;
    isInitialLoad.value = false;
  };

  const closeWizard = () => {
    localIsActive.value = false;
    currentStep.value = 0;
    isInitialLoad.value = false;
    if (props.isAdd) {
      itemData.value = JSON.parse(JSON.stringify(blankData));
      emit("update:itemEdit", {});
    }
    emit("close-wizard");
  };

  const prevStep = () => {
    if (currentStep.value > 0) {
      currentStep.value--;
    }
    emit("prev-step");
  };

  const nextStep = async () => {
    if (currentStep.value < finishAtStep.value) {
      currentStep.value++;
    }
    emit("next-step");
  };

  const createItem = async () => {
    const saveData = prepareDataForSave();

    isCreatingPanel.value = true;

    try {
      const response = await store.dispatch("bosch_panels/add", saveData);

      if (response && response.data) {
        itemData.value = {...itemData.value, ...response.data};
        originalData.value = JSON.parse(JSON.stringify(itemData.value));

        emit("refetch-data");
        return true;
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Error creating item:", error);
      toast({
        component: ToastificationContent,
        props: {
          title: "Error creating panel",
          text: error.response?.data?.message || "Error creating panel. Please try again.",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
      return false;
    } finally {
      isCreatingPanel.value = false;
    }
  };

  const loadData = () => {
    isLoading.value = true;
    if (!props.isAdd) {
      isInitialLoad.value = true;
      itemData.value = {...props.itemEdit};
      originalData.value = JSON.parse(JSON.stringify(props.itemEdit));

      getProfiles();

      if (props.itemEdit?.device?.units) {
        itemData.value.id_unit = props.itemEdit.device.units.map((value) => ({
          value: value.id,
          label: value.description,
          id_tenant: value.router.id_tenant,
        }));

        if (!props.isAdd) {
          originalData.value.id_unit = itemData.value.id_unit;
        }
      }

      // Cargar sectores para el centro seleccionado si existe
      if (props.itemEdit.id_center) {
        changeCenter(props.itemEdit.id_center);
      }

      setTimeout(() => {
        isInitialLoad.value = false;
      }, 100);
    }
    isLoading.value = false;
  };

  const prepareDataForSave = () => {
    const saveData = {...itemData.value};

    if (saveData.id_unit && Array.isArray(saveData.id_unit)) {
      saveData.id_unit = saveData.id_unit.map((unit) =>
        typeof unit === 'object' ? unit.value : unit
      );
    }

    if (saveData.active === null || saveData.active === undefined) {
      saveData.active = 1;
    }

    // Remover campos no requeridos para el POST si están vacíos
    if (!saveData.id_bosch_reseller) {
      delete saveData.id_bosch_reseller;
    }

    return saveData;
  };

  const handleSave = () => {
    const saveData = prepareDataForSave();

    let dispatch;
    if (props.isAdd) {
      dispatch = store.dispatch("bosch_panels/add", saveData);
    } else {
      const req = {
        id: itemData.value.id,
        params: saveData,
      };
      dispatch = store.dispatch("bosch_panels/edit", req);
    }

    isLoading.value = true;

    dispatch
      .then((response) => {
        toast({
          component: ToastificationContent,
          props: {
            title: props.isAdd ? "Panel created" : "Panel updated",
            text: props.isAdd
              ? "The Bosch panel has been created successfully."
              : "The Bosch panel has been updated successfully.",
            icon: "CheckIcon",
            variant: "success",
          },
        });

        if (props.isAdd) {
          itemData.value = {...itemData.value, ...response.data};
        }
        originalData.value = JSON.parse(JSON.stringify(itemData.value));

        emit("refetch-data");
      })
      .catch((error) => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: error.response?.data?.message || "An error occurred while saving the panel.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        isLoading.value = false;
      });
  };

  const onSubmit = async () => {
    // Validar formulario si existe la referencia
    if (generalStep.value && generalStep.value.refFormObserver) {
      const isValid = await generalStep.value.refFormObserver.validate();
      if (!isValid) {
        toast({
          component: ToastificationContent,
          props: {
            title: "Validation error",
            text: "Please complete all required fields",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        return;
      }
    }

    if (props.isAdd) {
      const success = await createItem();
      if (success) {
        toast({
          component: ToastificationContent,
          props: {
            title: "Configuration completed",
            text: "Bosch panel configured successfully",
            icon: "CheckIcon",
            variant: "success",
          },
        });
        closeWizard();
      }
      return;
    }

    const saveData = prepareDataForSave();
    const req = {
      id: itemData.value.id,
      params: saveData,
    };

    isLoading.value = true;

    try {
      await store.dispatch("bosch_panels/edit", req);

      toast({
        component: ToastificationContent,
        props: {
          title: "Configuration completed",
          text: "Bosch panel configured successfully",
          icon: "CheckIcon",
          variant: "success",
        },
      });

      originalData.value = JSON.parse(JSON.stringify(itemData.value));
      emit("refetch-data");
      closeWizard();
    } catch (error) {
      toast({
        component: ToastificationContent,
        props: {
          title: "Error",
          text: error.response?.data?.message || "An error occurred while updating the panel.",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    } finally {
      isLoading.value = false;
    }
  };

  const getProfiles = () => {
    store
      .dispatch("bosch_profiles/getAll")
      .then((response) => {
        let {data} = response;
        profileOptions.value = data;
      })
      .catch((error) => {
        console.error("Error al obtener perfiles:", error);
      });
  };

  const changeCenter = (centerId) => {
    if (centerId) {
      store.dispatch('sector/getAll', {center: centerId});
    }
  };

  watch(
    () => itemData.value.id_center,
    (newCenter, oldCenter) => {
      if (newCenter && newCenter !== oldCenter) {
        changeCenter(newCenter);
        // Solo resetear el sector si no estamos en carga inicial
        if (!isInitialLoad.value) {
          itemData.value.id_sector = null;
        }
      }
    }
  );

  return {
    currentStep,
    finishAtStep,
    isLoading,
    isCreatingPanel,
    isOpen,
    profileOptions,
    itemData,
    localIsActive,

    generalStep,

    wizardTitle,
    wizardSteps,
    wizardStepIcons,

    resetData,
    closeWizard,
    prevStep,
    nextStep,
    loadData,
    handleSave,
    onSubmit,
    createItem,
    changeCenter,
    hasChanges,
    toast,
    t,
  };
}
